import { coerceChartPanels } from "./assistantChartArtifactState";
import { coerceVisualization3DSpace } from "./assistantVisualization3DArtifactState";
import { coerceAssistantWorkbookInlinePreview } from "./assistantWorkbookArtifact";
import {
  ASSISTANT_ARTIFACT_HYDRATING_STATE,
  projectAssistantArtifactLongRunningWorkflow,
  toAssistantLongRunningWorkflowThreadState,
} from "./assistantArtifactLongRunningWorkflow";
import {
  hasRequiredArtifactPreviewForWorkflow,
  isContractedArtifactWorkflow,
} from "./assistantArtifactPromptRenderContract";
import type { AssistantTaskListItem } from "./assistantTaskLaneState";
import type {
  AdmissionsAssistantArtifactPreview,
  AdmissionsAssistantArtifactWorkspaceRef,
  AdmissionsAssistantDiagramPreview,
  AdmissionsAssistantLandingPagePreview,
  AdmissionsAssistantTaskThreadStatus,
} from "./types";

const toRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

export const formatArtifactExpiry = (
  expiresAtSeconds: unknown,
): string | undefined => {
  const numeric = Number(expiresAtSeconds);
  const expiresAtMs = Number.isFinite(numeric)
    ? numeric > 1_000_000_000_000
      ? numeric
      : numeric * 1000
    : null;
  if (!expiresAtMs) {
    return undefined;
  }
  const formatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return `Expires ${formatter.format(expiresAtMs)}`;
};

export const projectAssistantTaskThreadState = (input: {
  threadId: string;
  title: string;
  runId?: string | null;
  workItemId?: string | null;
  workflowKey?: string | null;
  tenantScopeStatus?: AdmissionsAssistantTaskThreadStatus["tenantScopeStatus"];
  degradedReason?: string | null;
  failureClass?: AdmissionsAssistantTaskThreadStatus["failureClass"];
  reasonCode?: string | null;
  failureDetail?: string | null;
  state: AdmissionsAssistantTaskThreadStatus["state"];
  stateLabel: string;
  statusMessage?: string | null;
  progressPercent?: number | null;
  artifactCount?: number;
}): AdmissionsAssistantTaskThreadStatus => ({
  threadId: input.threadId,
  title: input.title,
  runId: input.runId ?? null,
  workItemId: input.workItemId ?? null,
  workflowKey: input.workflowKey ?? null,
  tenantScopeStatus: input.tenantScopeStatus ?? "scoped",
  degradedReason: input.degradedReason ?? null,
  failureClass: input.failureClass ?? null,
  reasonCode: input.reasonCode ?? null,
  failureDetail: input.failureDetail ?? null,
  state: input.state,
  stateLabel: input.stateLabel,
  statusMessage: input.statusMessage ?? null,
  progressPercent: input.progressPercent ?? null,
  artifactCount: input.artifactCount ?? 0,
});

export const toArtifactWorkspaceRef = (
  value: unknown,
): AdmissionsAssistantArtifactWorkspaceRef | null => {
  const record = toRecord(value);
  if (!record || typeof record.downloadUrl !== "string") {
    return null;
  }
  return {
    id: String(record.id ?? ""),
    artifactType: String(record.artifactType ?? "report"),
    label: String(record.label ?? record.fileName ?? "Artifact"),
    fileName: String(record.fileName ?? "artifact"),
    downloadUrl: record.downloadUrl,
    mimeType: typeof record.mimeType === "string" ? record.mimeType : null,
    sourceRef: typeof record.sourceRef === "string" ? record.sourceRef : null,
    expiresAtLabel:
      typeof record.expiresAtEpochMs === "number"
        ? `Expires ${new Intl.DateTimeFormat("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          }).format(record.expiresAtEpochMs)}`
        : formatArtifactExpiry(record.expiresAt),
  };
};

export const resolveArtifactWorkspaceKey = (
  artifact: Pick<
    AdmissionsAssistantArtifactWorkspaceRef,
    "sourceRef" | "id" | "downloadUrl"
  >,
): string => {
  const sourceRef = String(artifact.sourceRef ?? "").trim();
  if (sourceRef) {
    return sourceRef;
  }
  const id = String(artifact.id ?? "").trim();
  if (id) {
    return id;
  }
  return artifact.downloadUrl;
};

const coerceArtifactWorkspaceRefs = (
  value: unknown,
): AdmissionsAssistantArtifactWorkspaceRef[] =>
  Array.isArray(value)
    ? value
        .map((entry) => toArtifactWorkspaceRef(entry))
        .filter((entry): entry is AdmissionsAssistantArtifactWorkspaceRef =>
          Boolean(entry),
        )
    : [];

const coerceLandingPagePreview = (
  value: unknown,
): AdmissionsAssistantLandingPagePreview | null => {
  const record = toRecord(value);
  if (!record) {
    return null;
  }
  const previewHtml = String(record.previewHtml ?? "").trim();
  if (!previewHtml) {
    return null;
  }
  const title =
    String(record.title ?? "Landing page draft").trim() || "Landing page draft";
  return {
    title,
    description:
      typeof record.description === "string"
        ? record.description.trim() || null
        : null,
    previewHtml,
    displayLabel:
      typeof record.displayLabel === "string"
        ? record.displayLabel.trim() || null
        : null,
  };
};

const coerceDiagramPreview = (
  value: unknown,
): AdmissionsAssistantDiagramPreview | null => {
  const record = toRecord(value);
  if (!record) {
    return null;
  }
  const mermaidSource = String(record.mermaidSource ?? "").trim();
  if (!mermaidSource) {
    return null;
  }
  const title =
    String(record.title ?? "Diagram draft").trim() || "Diagram draft";
  return {
    title,
    description:
      typeof record.description === "string"
        ? record.description.trim() || null
        : null,
    mermaidSource,
    diagramKind:
      typeof record.diagramKind === "string"
        ? record.diagramKind.trim() || null
        : null,
  };
};

const resolveSpreadsheetDownloadArtifact = (
  artifacts: readonly AdmissionsAssistantArtifactWorkspaceRef[],
): AdmissionsAssistantArtifactWorkspaceRef | null =>
  artifacts.find((artifact) =>
    artifact.fileName.toLowerCase().endsWith(".xlsx"),
  ) ??
  artifacts.find((artifact) =>
    artifact.mimeType?.includes("spreadsheetml.sheet"),
  ) ??
  artifacts.find((artifact) => artifact.artifactType === "spreadsheet") ??
  null;

const SPREADSHEET_WORKFLOW_KEYS = new Set([
  "artifact.spreadsheet.generate",
  "artifact.spreadsheet.modify",
]);

const CHART_WORKFLOW_KEYS = new Set(["artifact.chart.generate"]);

const VISUALIZATION3D_WORKFLOW_KEYS = new Set([
  "artifact.visualization3d.generate",
]);

const CUSTOM_PREVIEW_ARTIFACT_TYPES = new Set([
  "spreadsheet-workbook",
  "spreadsheet",
  "chart",
  "visualization3d",
  "html-page",
  "diagram",
]);

export const buildArtifactPreviewFromResponse = (input: {
  task: AssistantTaskListItem;
  response?: Record<string, unknown> | null;
}): AdmissionsAssistantArtifactPreview | null => {
  const debug = toRecord(input.response?.debug);
  const taskDebug = toRecord(debug?.task);
  const responseArtifacts = coerceArtifactWorkspaceRefs(debug?.artifactRefs);
  const taskArtifacts = input.task.artifacts.map((artifact) => ({
    ...artifact,
  }));
  const artifacts = [...responseArtifacts, ...taskArtifacts].filter(
    (artifact, index, collection) =>
      collection.findIndex(
        (candidate) =>
          resolveArtifactWorkspaceKey(candidate) ===
          resolveArtifactWorkspaceKey(artifact),
      ) === index,
  );

  const workbookArtifact =
    toArtifactWorkspaceRef(taskDebug?.workbookArtifactRef) ??
    artifacts.find(
      (artifact) => artifact.artifactType === "spreadsheet-workbook",
    ) ??
    null;
  const spreadsheetDownloadArtifact =
    toArtifactWorkspaceRef(taskDebug?.downloadArtifactRef) ??
    resolveSpreadsheetDownloadArtifact(artifacts);
  const workbookPreview = coerceAssistantWorkbookInlinePreview(
    taskDebug?.workbookPreview,
  );
  const chartArtifact =
    toArtifactWorkspaceRef(taskDebug?.chartArtifactRef) ??
    artifacts.find((artifact) => artifact.artifactType === "chart") ??
    null;
  const chartPanels = coerceChartPanels(taskDebug?.chartPanels);
  const visualization3DArtifact =
    toArtifactWorkspaceRef(taskDebug?.visualization3DArtifactRef) ??
    artifacts.find((artifact) => artifact.artifactType === "visualization3d") ??
    null;
  const visualization3DSpace = coerceVisualization3DSpace(
    taskDebug?.visualization3DSpace,
  );
  const visualization3DPanels = coerceChartPanels(
    taskDebug?.visualization3DPanels,
  );
  const landingPageArtifact =
    toArtifactWorkspaceRef(taskDebug?.landingPageArtifactRef) ??
    artifacts.find((artifact) => artifact.artifactType === "html-page") ??
    null;
  const landingPagePreview = coerceLandingPagePreview(
    taskDebug?.landingPagePreview,
  );
  const diagramArtifact =
    toArtifactWorkspaceRef(taskDebug?.diagramArtifactRef) ??
    artifacts.find((artifact) => artifact.artifactType === "diagram") ??
    null;
  const diagramPreview = coerceDiagramPreview(taskDebug?.diagramPreview);
  const artifactCards = artifacts.filter(
    (artifact) => !CUSTOM_PREVIEW_ARTIFACT_TYPES.has(artifact.artifactType),
  );

  if (
    !workbookArtifact &&
    !chartArtifact &&
    chartPanels.length === 0 &&
    !visualization3DArtifact &&
    !visualization3DSpace &&
    visualization3DPanels.length === 0 &&
    !landingPageArtifact &&
    !landingPagePreview &&
    !diagramArtifact &&
    !diagramPreview &&
    artifactCards.length === 0
  ) {
    return null;
  }
  return {
    workbookArtifact,
    spreadsheetDownloadArtifact,
    workbookPreview,
    chartArtifact,
    chartPanels,
    visualization3DArtifact,
    visualization3DSpace,
    visualization3DPanels,
    landingPageArtifact,
    landingPagePreview,
    diagramArtifact,
    diagramPreview,
    artifactCards,
  };
};

export const countArtifactPreviewItems = (
  preview: AdmissionsAssistantArtifactPreview | null | undefined,
): number =>
  preview
    ? [
        preview.workbookArtifact,
        preview.spreadsheetDownloadArtifact,
        preview.chartArtifact,
        preview.visualization3DArtifact,
        preview.landingPageArtifact,
        preview.diagramArtifact,
        ...(preview.artifactCards ?? []),
      ].filter(Boolean).length
    : 0;

export const shouldDeferCompletedArtifactAnnouncement = (input: {
  task: Pick<AssistantTaskListItem, "workflowKey">;
  workflow: Pick<
    ReturnType<typeof projectAssistantArtifactLongRunningWorkflow>,
    "isArtifactWorkflow" | "state"
  >;
  artifactPreview?: AdmissionsAssistantArtifactPreview | null;
}): boolean => {
  if (!input.workflow.isArtifactWorkflow) {
    return false;
  }
  if (input.workflow.state === ASSISTANT_ARTIFACT_HYDRATING_STATE) {
    return true;
  }

  const workflowKey = String(input.task.workflowKey ?? "").trim();
  if (SPREADSHEET_WORKFLOW_KEYS.has(workflowKey)) {
    return (
      !input.artifactPreview?.workbookArtifact ||
      !input.artifactPreview?.workbookPreview
    );
  }
  if (CHART_WORKFLOW_KEYS.has(workflowKey)) {
    return (
      !input.artifactPreview?.chartArtifact &&
      (input.artifactPreview?.chartPanels?.length ?? 0) === 0
    );
  }
  if (VISUALIZATION3D_WORKFLOW_KEYS.has(workflowKey)) {
    return !hasRequiredArtifactPreviewForWorkflow({
      workflowKey,
      artifactPreview: input.artifactPreview ?? null,
    });
  }
  if (isContractedArtifactWorkflow(workflowKey)) {
    return !hasRequiredArtifactPreviewForWorkflow({
      workflowKey,
      artifactPreview: input.artifactPreview ?? null,
    });
  }
  return !input.artifactPreview;
};

export const projectAssistantTaskWorkflow = (input: {
  task: AssistantTaskListItem;
  response?: Record<string, unknown> | null;
  artifactPreview?: AdmissionsAssistantArtifactPreview | null;
}) =>
  projectAssistantArtifactLongRunningWorkflow({
    taskClass: input.task.taskClass,
    workflowKey: input.task.workflowKey,
    state: input.task.state,
    phaseLabel: input.task.phaseLabel,
    statusMessage: input.task.statusMessage,
    progressPercent: input.task.progressPercent,
    resultSummary: input.task.resultSummary,
    artifacts: input.task.artifacts,
    response: input.response ?? null,
    artifactPreview: input.artifactPreview ?? null,
  });

export const resolveConversationTaskThreadState = (input: {
  task: AssistantTaskListItem;
  threadId: string;
  response?: Record<string, unknown> | null;
  artifactPreview?: AdmissionsAssistantArtifactPreview | null;
}): AdmissionsAssistantTaskThreadStatus => {
  const workflow = projectAssistantTaskWorkflow({
    task: input.task,
    response: input.response,
    artifactPreview: input.artifactPreview ?? null,
  });
  const workflowKey = String(input.task.workflowKey ?? "").trim();
  const completedChartMissingPanels =
    CHART_WORKFLOW_KEYS.has(workflowKey) &&
    input.task.state === "completed" &&
    Boolean(input.artifactPreview?.chartArtifact) &&
    (input.artifactPreview?.chartPanels?.length ?? 0) === 0;
  return projectAssistantTaskThreadState({
    threadId: input.threadId,
    runId: input.task.runId,
    workItemId: input.task.id,
    workflowKey: input.task.workflowKey,
    title: input.task.title,
    state: completedChartMissingPanels
      ? "failed"
      : toAssistantLongRunningWorkflowThreadState(workflow.state),
    stateLabel: completedChartMissingPanels
      ? "Chart preview unavailable"
      : (workflow.phaseLabel ?? input.task.phaseLabel ?? input.task.state),
    statusMessage: completedChartMissingPanels
      ? "The chart artifact completed, but no renderable chart panels were available."
      : (workflow.resultSummary ?? workflow.statusMessage ?? null),
    progressPercent: workflow.progressPercent,
    tenantScopeStatus: input.task.tenantScopeStatus,
    degradedReason: input.task.degradedReason,
    failureClass: completedChartMissingPanels ? "domain-readiness" : undefined,
    reasonCode: completedChartMissingPanels
      ? "chart-inline-preview-missing"
      : undefined,
    failureDetail: completedChartMissingPanels
      ? "Completed chart tasks must hydrate chartPanels before the assistant can render the inline preview."
      : undefined,
    artifactCount: Math.max(
      countArtifactPreviewItems(input.artifactPreview),
      input.task.artifacts.length,
    ),
  });
};
